/* eslint-disable react-refresh/only-export-components -- util fn exported alongside component */
import { cn } from '@/lib/cn'

export interface MonogramProps {
  /** Display name — initials are derived from it. */
  name: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
  shape?: 'circle' | 'rounded'
  className?: string
}

const SIZE_CLASSES: Record<NonNullable<MonogramProps['size']>, string> = {
  xs: 'size-4 text-[9px]',
  sm: 'size-8 text-[11px]',
  md: 'size-10 text-sm',
  lg: 'size-14 text-lg',
}

const SHAPE_CLASSES: Record<NonNullable<MonogramProps['shape']>, string> = {
  circle: 'rounded-full',
  rounded: 'rounded-lg',
}

/** First letters of up to two words; splits on whitespace and common separators (., @, _). */
export function monogramInitials(str: string): string {
  const parts = str.trim().split(/[\s@._-]+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Monogram — initials on the design system's own indigo. ONE treatment, everywhere.
 *
 * Replaces `SeedAvatar`, which derived a per-identity hue by hashing a seed string into
 * `oklch(0.52 0.14 <hash % 360>)` and drew white on it. That produced a different saturated
 * colour per row. Three reasons it went:
 *
 *  1. **Contrast.** The seed hue swept all 360°, so the worst case was 4.78:1 — clearing AA only
 *     because the lightness had already been pulled down from 0.55 to 0.52 after a sweep found it
 *     failing on a fifth of the hue circle. This is 7.32:1 in light and 7.90:1 in dark.
 *  2. **Dark mode.** The seed colour was a literal OKLCH triple, identical in both themes. These
 *     are tokens, so they actually respond to the theme.
 *  3. **It was not decodable.** The seed was usually a tenant UUID, so the colour carried no
 *     meaning a user could read — "Ashcombe is the orange one" is only learnable, never derivable,
 *     and it changed as soon as a row moved. The initials already identify the row.
 *
 * `ClientMonogram` (`components/clients/`) had this exact treatment and is deleted in favour of
 * this. Note that reverses the audit's task 5.12, which proposed merging it INTO `SeedAvatar` —
 * the surviving implementation is the one whose visual design was kept, not the one with more
 * call sites.
 */
export function Monogram({ name, size = 'md', shape = 'circle', className }: MonogramProps) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center font-display font-bold',
        'bg-primary-subtle text-primary',
        SIZE_CLASSES[size],
        SHAPE_CLASSES[shape],
        className,
      )}
      aria-hidden
    >
      {monogramInitials(name)}
    </span>
  )
}
